import { EMBARK_PORTS, destinationsFrom, getPort } from '@realseas/shared';
import type { CaptainSave, Port } from '@realseas/shared';
import type { Connection } from '../Connection.js';
import { setHTML, esc } from '../dom.js';

function distanceBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.round(Math.sqrt(dx * dx + dy * dy));
}

/**
 * Two jobs, one screen:
 *  - status 'picking_port': a brand-new captain chooses where in Europe to embark
 *    (every first voyage is bound for Tortuga).
 *  - status 'picking_destination': a captain who has just made port chooses the
 *    next port of call. From Tortuga that is any European port; from Europe it
 *    is Tortuga and nothing else.
 */
export function renderPortScreen(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
): void {
  const choosingDestination = captain.status === 'picking_destination';
  const here = choosingDestination && captain.portId ? getPort(captain.portId) : undefined;
  const tortuga = getPort('tortuga')!;

  const ports: Port[] = choosingDestination ? destinationsFrom(captain.portId) : EMBARK_PORTS;
  // Distance shown on each card: from the card's port to Tortuga when embarking,
  // from the current port to the card's port when choosing the next leg.
  const from = here?.position ?? null;

  const cards = ports.map(p => {
    const dist = from ? distanceBetween(from, p.position) : distanceBetween(p.position, tortuga.position);
    const distLabel = from ? `${dist.toLocaleString()} km from ${esc(here!.name)}` : `${dist.toLocaleString()} km to Tortuga`;
    return `
      <div class="origin-card port-card" data-port="${esc(p.id)}">
        <div class="port-flag" aria-hidden="true">${esc(flagFor(p.id))}</div>
        <h3>${esc(p.name)}</h3>
        <div class="epithet">${esc(p.country)}</div>
        <div class="divider"></div>
        <p class="blurb">${esc(p.flavor)}</p>
        <div class="stats">
          <div class="reales">${distLabel}</div>
          <div class="bonus">${esc(p.difficulty)}</div>
        </div>
      </div>
    `;
  }).join('');

  const title = choosingDestination ? 'Next Port of Call' : 'Port of Embarkation';
  const subtitle = choosingDestination
    ? `Captain ${esc(captain.username)}, where are you bound from ${esc(here?.name ?? 'here')}?`
    : `Captain ${esc(captain.username)}, from where will you sail?`;
  const lede = choosingDestination
    ? (here?.region === 'caribbean'
        ? 'Any harbor in Europe will take your cargo. Pick one; its bearing will be marked on your compass.'
        : 'From Europe there is only one road: back across the Atlantic to Tortuga.')
    : 'All first voyages end at Tortuga. The path you must take depends on where you start.';

  setHTML(container, `
    <h1 class="title">${title}</h1>
    <p class="subtitle">${subtitle}</p>
    <p style="text-align: center; color: var(--ink-faint); font-style: italic; margin-bottom: 18px;">
      ${lede}
    </p>
    <div class="origin-grid port-grid">${cards}</div>
  `);

  container.querySelectorAll<HTMLElement>('.port-card').forEach(card => {
    card.onclick = () => {
      const id = card.dataset.port!;
      if (choosingDestination) conn.send({ type: 'pick_destination', portId: id });
      else conn.send({ type: 'pick_port', portId: id });
    };
  });
}

function flagFor(portId: string): string {
  switch (portId) {
    case 'cadiz':      return '♛';
    case 'lisbon':     return '✚';
    case 'brest':      return '⚜';
    case 'plymouth':   return '✠';
    case 'copenhagen': return '✣';
    case 'tortuga':    return '☠';
    default:           return '⚓';
  }
}
